import { useGLTF } from "@react-three/drei";
import {
  ITochiChicken,
  ITochiItem,
  ITochiObjectItemHide,
} from "@snackpass/snackpass-types";
import { useEffect } from "react";
import { RIG_NAME } from "src/utils/constants";
import { getTochiMaterial } from "src/utils/Tochi/TochiMaterials/getTochiMaterial";
import TochiMaterial from "src/utils/Tochi/TochiMaterials/TochiMaterial";
import { loadShaderChunks } from "src/utils/Tochi/TochiShaders/TochiShaderChunks";
import { ITochiChickenAsset, ITochiChickenEye } from "src/utils/types";
import { suspend } from "suspend-react";
import { AnimationObjectGroup } from "three";
import { getEyeName, getHairName, timeout } from "./utils";

type Props = {
  chicken: ITochiChicken;
  items: ITochiItem[];
  animGroup: AnimationObjectGroup;
  asset: ITochiChickenAsset;
  eye: ITochiChickenEye;
  disableBlinking?: boolean;
  hide?: boolean;
};

export default function TochiChicken({
  chicken,
  items,
  animGroup,
  asset,
  eye,
  hide,
  disableBlinking,
}: Props) {
  // Fetch model and a separate texture

  const { scene } = useGLTF(asset.rig.asset);
  const shouldBlink = !disableBlinking;

  useEffect(() => {
    hideItemSubmeshes(items);
  }, [items]);

  // TODO traversing after the effect isn't best practice
  // https://github.com/pmndrs/react-three-fiber/discussions/897
  useEffect(() => {
    if (!scene) return;
    animGroup.add(scene);
  }, [scene]);

  const handleSetMaterials = async () => {
    loadShaderChunks();
    const body = getTochiMaterial(asset.body);
    const addons = getTochiMaterial(asset.addons);
    const eyes1 = getTochiMaterial(asset.eyes1);
    const eyes2 = getTochiMaterial(asset.eyes2);
    await loadEyes(eyes1);
    await loadEyes(eyes2);
    const [bodyShader, addonsShader, eyes1Shader, eyes2Shader] =
      await Promise.all([body.get(), addons.get(), eyes1.get(), eyes2.get()]);

    scene.traverse(async (child: any) => {
      if (child.isMesh) {
        child.castShadow = true;
        child.receiveShadow = false;
        switch (child.name) {
          case `${RIG_NAME}Chicken_Body`:
          case `${RIG_NAME}Chicken_Hair1`:
          case `${RIG_NAME}Chicken_Hair2`:
          case `${RIG_NAME}Chicken_Hair3`:
          case `${RIG_NAME}Chicken_Hair4`:
          case `${RIG_NAME}Chicken_Hair5`:
          case `${RIG_NAME}Chicken_Tail_1`:
          case `${RIG_NAME}Chicken_Wings`:
            child.material = bodyShader;
            break;
          case `${RIG_NAME}Chicken_Beak`:
          case `${RIG_NAME}Chicken_Feet`:
            child.material = addonsShader;
            break;
          case `${RIG_NAME}Chicken_Eyes_Default`:
          case `${RIG_NAME}Chicken_Eyes_Closed`:
          case `${RIG_NAME}Chicken_Eyes_Up`:
          case `${RIG_NAME}Chicken_Eyes_Down`:
          case `${RIG_NAME}Chicken_Eyes_Left`:
          case `${RIG_NAME}Chicken_Eyes_Right`:
            child.material = eyes1Shader;
            child.visible = false;
            break;
          case `${RIG_NAME}Chicken_Eyes_Mad`:
          case `${RIG_NAME}Chicken_Eyes_Squeeze`:
          case `${RIG_NAME}Chicken_Eyes_Suprise`:
          case `${RIG_NAME}Chicken_Eyes_Droopy`:
          case `${RIG_NAME}Chicken_Eyes_Sparkle`:
          case `${RIG_NAME}Chicken_Eyes_UpClosed`:
            child.material = eyes2Shader;
            child.visible = false;
            break;
          default:
            console.log("Hiding unrecognized submesh " + child.name);
            child.visible = false;
        }
      }
    });
    body.setChicken(chicken);
    eyes1.setChicken(chicken);
    eyes2.setChicken(chicken);
    setHair();
    hideItemSubmeshes(items);
  };

  const loadEyes = async (eyes: TochiMaterial) => {
    await eyes.load();
    (await eyes.get()).transparent = true;
    (await eyes.get()).polygonOffset = true;
    (await eyes.get()).polygonOffsetFactor = -0.1;
    (await eyes.get()).uniforms.fresnelOpacity.value = 0;
    (await eyes.get()).uniforms.fresnelOpacity.value = 0;
  };

  const hideItemSubmeshes = (items: ITochiItem[]) => {
    const toHide = new Set<ITochiObjectItemHide>();
    items.forEach((item) => {
      if (item.hide) {
        item.hide.forEach(toHide.add, toHide);
      }
    });
    setSubmeshes(Array.from(toHide));
  };

  const setSubmeshes = async (hideSubmeshes: ITochiObjectItemHide[]) => {
    if (hideSubmeshes.includes("HAIR")) {
      hideAllHair();
    } else {
      setHair();
    }
    setSubmeshVisible(
      `${RIG_NAME}Chicken_Tail_1`,
      !hideSubmeshes.includes("TAIL")
    );
    setSubmeshVisible(
      `${RIG_NAME}Chicken_Feet`,
      !hideSubmeshes.includes("FEET")
    );
  };

  const setSubmeshVisible = (name: string, visible: boolean) => {
    if (!scene) return;
    const submesh = scene.getObjectByName(name);
    if (submesh) submesh.visible = visible;
  };

  const hideAllHair = () => {
    setSubmeshVisible(getHairName(1), false);
    setSubmeshVisible(getHairName(2), false);
    setSubmeshVisible(getHairName(3), false);
    setSubmeshVisible(getHairName(4), false);
    setSubmeshVisible(getHairName(5), false);
  };

  const setHair = () => {
    hideAllHair();
    setSubmeshVisible(getHairName(chicken.genes.hair), true);
  };

  const hideAllEyes = () => {
    setSubmeshVisible(getEyeName("Default"), false);
    setSubmeshVisible(getEyeName("Closed"), false);
    setSubmeshVisible(getEyeName("Up"), false);
    setSubmeshVisible(getEyeName("Down"), false);
    setSubmeshVisible(getEyeName("Left"), false);
    setSubmeshVisible(getEyeName("Right"), false);
    setSubmeshVisible(getEyeName("Mad"), false);
    setSubmeshVisible(getEyeName("Squeeze"), false);
    setSubmeshVisible(getEyeName("Surprise"), false);
    setSubmeshVisible(getEyeName("Droopy"), false);
    setSubmeshVisible(getEyeName("Sparkle"), false);
    setSubmeshVisible(getEyeName("UpClosed"), false);
  };

  const blink = async () => {
    await timeout(Math.random() * 4000 + 5000);

    if (shouldBlink) {
      hideAllEyes();
      setSubmeshVisible(getEyeName("Closed"), true);
      await timeout(20);
      if (shouldBlink) {
        hideAllEyes();
        setSubmeshVisible(getEyeName("Default"), true);
      }
    }
    blink();
  };

  const loadedScene = suspend(async () => {
    if (!scene) return;
    await handleSetMaterials();
    return scene;
  }, [scene]);

  useEffect(() => {
    blink();
  }, []);

  useEffect(() => {
    if (!loadedScene) return;
    hideAllEyes();
    setSubmeshVisible(getEyeName(eye), true);
  }, [eye]);

  return <primitive object={loadedScene} visible={!hide} />;
}
